import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api/sessions/marketing';

const C = {
  accent:'#3AAF58', accentLight:'#EAF7EE',
  bg:'#F3F6F3', surface:'#FFFFFF',
  border:'#E2E8E2', border2:'#CDD8CD',
  text:'#1A2E1E', text2:'#3D5E44', text3:'#7A9A80',
  amber:'#D4840F', amberBg:'#FEF6E7', amberBorder:'#F0D499',
  red:'#C83333', redBg:'#FDEAEA',
  blue:'#2563A8', blueBg:'#EAF1FB', blueBorder:'#B8D0F0',
  purple:'#7C3AED', purpleBg:'#F3EFFD',
  primary:'#153D4B',
};

const inr = n => '₹' + Number(Math.round(n)||0).toLocaleString('en-IN');

const STAGES = ['New','Contacted','Qualified','Proposal Sent','Negotiation','Converted','Lost'];
const SOURCES = ['Direct','Instagram','Facebook','WhatsApp','Website','OTA','Referral','Phone','Email','Google Ads','Other'];
const EVENT_TYPES = ['Wedding','Corporate Outing','School Picnic','Birthday','Anniversary','Conference','Room Stay','Day Outing','Other'];
const CHANNELS = ['Social Media','Google Ads','WhatsApp','Email','OTA','Referral','Print','Outdoor','Events','Direct'];
const PLATFORMS = ['Instagram','Facebook','WhatsApp','LinkedIn','YouTube','Twitter','Google'];
const CONTENT_TYPES = ['Post','Story','Reel','Video','Blog','Email','Ad','Event'];

const inp = { width:'100%', padding:'9px 12px', background:'#F3F6F3', border:'1.5px solid #CDD8CD', borderRadius:8, color:'#1A2E1E', fontSize:13, boxSizing:'border-box' };
const lbl = t => <div style={{fontSize:11,fontWeight:700,color:'#7A9A80',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:5}}>{t}</div>;

const STAGE_COLORS = {
  New:{bg:'#EAF1FB',color:'#2563A8'},
  Contacted:{bg:'#FEF6E7',color:'#D4840F'},
  Qualified:{bg:'#F3EFFD',color:'#7C3AED'},
  'Proposal Sent':{bg:'#EAF7EE',color:'#3AAF58'},
  Negotiation:{bg:'#FFF3E0',color:'#E65100'},
  Converted:{bg:'#EAF7EE',color:'#1B7A3E'},
  Lost:{bg:'#FDEAEA',color:'#C83333'},
};

const StageBadge = ({stage}) => {
  const s = STAGE_COLORS[stage]||{bg:C.bg,color:C.text3};
  return <span style={{padding:'3px 10px',borderRadius:99,fontSize:11,fontWeight:700,...s}}>{stage}</span>;
};

const StatCard = ({label,value,sub,color,icon}) => (
  <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:'16px 20px',borderTop:`3px solid ${color||C.accent}`}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
      <div>
        <div style={{fontSize:11,fontWeight:700,color:C.text3,textTransform:'uppercase',marginBottom:6}}>{label}</div>
        <div style={{fontSize:24,fontWeight:900,color:color||C.text}}>{value}</div>
        {sub&&<div style={{fontSize:11,color:C.text3,marginTop:4}}>{sub}</div>}
      </div>
      {icon&&<div style={{fontSize:28,opacity:.8}}>{icon}</div>}
    </div>
  </div>
);

const TABS = [
  {id:'dashboard',label:'Dashboard',icon:'📊'},
  {id:'leads',label:'Lead Pipeline',icon:'🎯'},
  {id:'campaigns',label:'Campaigns',icon:'📣'},
  {id:'social',label:'Social Media',icon:'📱'},
  {id:'content',label:'Content Calendar',icon:'📅'},
  {id:'budget',label:'Budget',icon:'💰'},
];

export default function Marketing() {
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [socialLeads, setSocialLeads] = useState([]);
  const [content, setContent] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  // Modals
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [showContentForm, setShowContentForm] = useState(false);
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterSource, setFilterSource] = useState('');

  // Forms
  const initLead = { name:'', phone:'', email:'', source:'Direct', stage:'New', event_type:'Wedding', value:0, assigned_to:'', follow_up_date:'', notes:'', campaign:'' };
  const initCampaign = { name:'', type:'Digital', channel:'Social Media', status:'Planning', target_audience:'', budget:0, spent:0, leads_generated:0, conversions:0, revenue_generated:0, start_date:'', end_date:'', description:'' };
  const initContent = { title:'', platform:'Instagram', content_type:'Post', scheduled_date:'', scheduled_time:'', content:'', assigned_to:'', tags:'' };
  const initBudget = { month:new Date().getMonth()+1, year:new Date().getFullYear(), channel:'Social Media', allocated:0, spent:0, notes:'' };

  const [leadForm, setLeadForm] = useState(initLead);
  const [campaignForm, setCampaignForm] = useState(initCampaign);
  const [contentForm, setContentForm] = useState(initContent);
  const [budgetForm, setBudgetForm] = useState(initBudget);

  const showMsg = m => { setMsg(m); setTimeout(()=>setMsg(''),4000); };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, leadsRes, campRes, socialRes, contentRes, budgetRes] = await Promise.all([
        fetch(`${API}/dashboard`).then(r=>r.json()),
        fetch(`${API}/leads`).then(r=>r.json()),
        fetch(`${API}/campaigns`).then(r=>r.json()),
        fetch(`${API}/social-leads`).then(r=>r.json()),
        fetch(`${API}/content`).then(r=>r.json()),
        fetch(`${API}/budgets`).then(r=>r.json()),
      ]);
      setDashboard(dash);
      setLeads(Array.isArray(leadsRes)?leadsRes:[]);
      setCampaigns(Array.isArray(campRes)?campRes:[]);
      setSocialLeads(Array.isArray(socialRes)?socialRes:[]);
      setContent(Array.isArray(contentRes)?contentRes:[]);
      setBudgets(Array.isArray(budgetRes)?budgetRes:[]);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(()=>{ loadAll(); },[loadAll]);

  const saveLead = async () => {
    if (!leadForm.name) { showMsg('Name required'); return; }
    setSaving(true);
    try {
      const url = editItem ? `${API}/leads/${editItem.id}` : `${API}/leads`;
      const method = editItem ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers:{'Content-Type':'application/json'}, body:JSON.stringify(leadForm) });
      if (r.ok) { setShowLeadForm(false); setEditItem(null); setLeadForm(initLead); loadAll(); showMsg('Lead saved!'); }
      else showMsg('Failed');
    } catch(e) { showMsg('Error'); }
    setSaving(false);
  };

  const saveCampaign = async () => {
    if (!campaignForm.name) { showMsg('Campaign name required'); return; }
    setSaving(true);
    try {
      const url = editItem ? `${API}/campaigns/${editItem.id}` : `${API}/campaigns`;
      const method = editItem ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers:{'Content-Type':'application/json'}, body:JSON.stringify(campaignForm) });
      if (r.ok) { setShowCampaignForm(false); setEditItem(null); setCampaignForm(initCampaign); loadAll(); showMsg('Campaign saved!'); }
      else showMsg('Failed');
    } catch(e) { showMsg('Error'); }
    setSaving(false);
  };

  const saveContent = async () => {
    if (!contentForm.title) { showMsg('Title required'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/content`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(contentForm) });
      if (r.ok) { setShowContentForm(false); setContentForm(initContent); loadAll(); showMsg('Content scheduled!'); }
      else showMsg('Failed');
    } catch(e) { showMsg('Error'); }
    setSaving(false);
  };

  const saveBudget = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/budgets`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(budgetForm) });
      if (r.ok) { setShowBudgetForm(false); setBudgetForm(initBudget); loadAll(); showMsg('Budget saved!'); }
      else showMsg('Failed');
    } catch(e) { showMsg('Error'); }
    setSaving(false);
  };

  const updateLeadStage = async (id, stage) => {
    await fetch(`${API}/leads/${id}`, { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify({...leads.find(l=>l.id===id), stage}) });
    loadAll();
  };

  const filteredLeads = leads.filter(l => {
    const matchSearch = !search || l.name?.toLowerCase().includes(search.toLowerCase()) || l.phone?.includes(search);
    const matchStage = !filterStage || l.stage===filterStage;
    const matchSource = !filterSource || l.source===filterSource;
    return matchSearch && matchStage && matchSource;
  });

  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  // ── LEAD FORM MODAL ──
  const LeadModal = () => (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:C.surface,borderRadius:16,width:'100%',maxWidth:600,maxHeight:'90vh',overflowY:'auto',padding:28}}>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:20}}>
          <div style={{fontSize:17,fontWeight:800,color:C.text}}>{editItem?'Edit Lead':'New Lead'}</div>
          <button onClick={()=>{setShowLeadForm(false);setEditItem(null);setLeadForm(initLead);}} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 12px',cursor:'pointer'}}>Cancel</button>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div style={{gridColumn:'span 2'}}>{lbl('Client Name *')}<input style={inp} value={leadForm.name} onChange={e=>setLeadForm(p=>({...p,name:e.target.value}))} placeholder="Name or Company" /></div>
          <div>{lbl('Phone')}<input style={inp} value={leadForm.phone} onChange={e=>setLeadForm(p=>({...p,phone:e.target.value}))} /></div>
          <div>{lbl('Email')}<input style={inp} value={leadForm.email} onChange={e=>setLeadForm(p=>({...p,email:e.target.value}))} /></div>
          <div>{lbl('Source')}<select style={inp} value={leadForm.source} onChange={e=>setLeadForm(p=>({...p,source:e.target.value}))}>{SOURCES.map(s=><option key={s}>{s}</option>)}</select></div>
          <div>{lbl('Event Type')}<select style={inp} value={leadForm.event_type} onChange={e=>setLeadForm(p=>({...p,event_type:e.target.value}))}>{EVENT_TYPES.map(s=><option key={s}>{s}</option>)}</select></div>
          <div>{lbl('Stage')}<select style={inp} value={leadForm.stage} onChange={e=>setLeadForm(p=>({...p,stage:e.target.value}))}>{STAGES.map(s=><option key={s}>{s}</option>)}</select></div>
          <div>{lbl('Lead Value (Rs)')}<input style={inp} type="number" value={leadForm.value} onChange={e=>setLeadForm(p=>({...p,value:e.target.value}))} /></div>
          <div>{lbl('Assigned To')}<input style={inp} value={leadForm.assigned_to} onChange={e=>setLeadForm(p=>({...p,assigned_to:e.target.value}))} /></div>
          <div>{lbl('Follow Up Date')}<input style={inp} type="date" value={leadForm.follow_up_date} onChange={e=>setLeadForm(p=>({...p,follow_up_date:e.target.value}))} /></div>
          <div>{lbl('Campaign')}<input style={inp} value={leadForm.campaign} onChange={e=>setLeadForm(p=>({...p,campaign:e.target.value}))} placeholder="Campaign name" /></div>
          <div style={{gridColumn:'span 2'}}>{lbl('Notes')}<textarea style={{...inp,height:70,resize:'vertical'}} value={leadForm.notes} onChange={e=>setLeadForm(p=>({...p,notes:e.target.value}))} /></div>
        </div>
        <button onClick={saveLead} disabled={saving} style={{width:'100%',padding:'12px',background:C.accent,color:'#fff',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer',marginTop:16,opacity:saving?.6:1}}>
          {saving?'Saving...':'Save Lead'}
        </button>
      </div>
    </div>
  );

  // ── CAMPAIGN FORM MODAL ──
  const CampaignModal = () => (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:C.surface,borderRadius:16,width:'100%',maxWidth:600,maxHeight:'90vh',overflowY:'auto',padding:28}}>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:20}}>
          <div style={{fontSize:17,fontWeight:800,color:C.text}}>{editItem?'Edit Campaign':'New Campaign'}</div>
          <button onClick={()=>{setShowCampaignForm(false);setEditItem(null);setCampaignForm(initCampaign);}} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:'6px 12px',cursor:'pointer'}}>Cancel</button>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div style={{gridColumn:'span 2'}}>{lbl('Campaign Name *')}<input style={inp} value={campaignForm.name} onChange={e=>setCampaignForm(p=>({...p,name:e.target.value}))} /></div>
          <div>{lbl('Type')}<select style={inp} value={campaignForm.type} onChange={e=>setCampaignForm(p=>({...p,type:e.target.value}))}>{['Digital','Offline','Event','PR','Both'].map(s=><option key={s}>{s}</option>)}</select></div>
          <div>{lbl('Channel')}<select style={inp} value={campaignForm.channel} onChange={e=>setCampaignForm(p=>({...p,channel:e.target.value}))}>{CHANNELS.map(s=><option key={s}>{s}</option>)}</select></div>
          <div>{lbl('Status')}<select style={inp} value={campaignForm.status} onChange={e=>setCampaignForm(p=>({...p,status:e.target.value}))}>{['Planning','Active','Paused','Completed','Cancelled'].map(s=><option key={s}>{s}</option>)}</select></div>
          <div>{lbl('Target Audience')}<input style={inp} value={campaignForm.target_audience} onChange={e=>setCampaignForm(p=>({...p,target_audience:e.target.value}))} placeholder="e.g. Couples 25-40" /></div>
          <div>{lbl('Budget (Rs)')}<input style={inp} type="number" value={campaignForm.budget} onChange={e=>setCampaignForm(p=>({...p,budget:e.target.value}))} /></div>
          <div>{lbl('Spent (Rs)')}<input style={inp} type="number" value={campaignForm.spent} onChange={e=>setCampaignForm(p=>({...p,spent:e.target.value}))} /></div>
          <div>{lbl('Start Date')}<input style={inp} type="date" value={campaignForm.start_date} onChange={e=>setCampaignForm(p=>({...p,start_date:e.target.value}))} /></div>
          <div>{lbl('End Date')}<input style={inp} type="date" value={campaignForm.end_date} onChange={e=>setCampaignForm(p=>({...p,end_date:e.target.value}))} /></div>
          <div>{lbl('Leads Generated')}<input style={inp} type="number" value={campaignForm.leads_generated} onChange={e=>setCampaignForm(p=>({...p,leads_generated:e.target.value}))} /></div>
          <div>{lbl('Conversions')}<input style={inp} type="number" value={campaignForm.conversions} onChange={e=>setCampaignForm(p=>({...p,conversions:e.target.value}))} /></div>
          <div style={{gridColumn:'span 2'}}>{lbl('Description')}<textarea style={{...inp,height:60,resize:'vertical'}} value={campaignForm.description} onChange={e=>setCampaignForm(p=>({...p,description:e.target.value}))} /></div>
        </div>
        <button onClick={saveCampaign} disabled={saving} style={{width:'100%',padding:'12px',background:C.accent,color:'#fff',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer',marginTop:16,opacity:saving?.6:1}}>
          {saving?'Saving...':'Save Campaign'}
        </button>
      </div>
    </div>
  );

  if (loading) return <div style={{padding:60,textAlign:'center',color:C.text3}}>Loading Marketing Dashboard...</div>;

  const stats = dashboard?.stats||{};
  const convRate = stats.total_leads>0 ? Math.round(stats.converted/stats.total_leads*100) : 0;

  return (
    <div style={{padding:'24px 28px',background:C.bg,minHeight:'100vh'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:C.text}}>Marketing Command Centre</div>
          <div style={{fontSize:13,color:C.text3,marginTop:2}}>Live data — Leads, Campaigns, Social Media & Content</div>
        </div>
        <div style={{display:'flex',gap:8}}>
          {tab==='leads'&&<button onClick={()=>{setEditItem(null);setLeadForm(initLead);setShowLeadForm(true);}} style={{padding:'9px 16px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>+ Add Lead</button>}
          {tab==='campaigns'&&<button onClick={()=>{setEditItem(null);setCampaignForm(initCampaign);setShowCampaignForm(true);}} style={{padding:'9px 16px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>+ New Campaign</button>}
          {tab==='content'&&<button onClick={()=>setShowContentForm(true)} style={{padding:'9px 16px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>+ Schedule Content</button>}
          {tab==='budget'&&<button onClick={()=>setShowBudgetForm(true)} style={{padding:'9px 16px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>+ Add Budget</button>}
          <button onClick={loadAll} style={{padding:'9px 14px',background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,color:C.text2,cursor:'pointer'}}>↻ Refresh</button>
        </div>
      </div>

      {msg&&<div style={{padding:'10px 16px',borderRadius:10,marginBottom:16,fontSize:13,fontWeight:600,background:C.accentLight,color:C.accent,border:`1px solid ${C.accent}`}}>{msg}</div>}

      {/* Tabs */}
      <div style={{display:'flex',gap:6,marginBottom:20,flexWrap:'wrap'}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{padding:'8px 16px',background:tab===t.id?C.accent:C.surface,color:tab===t.id?'#fff':C.text2,border:`1px solid ${tab===t.id?C.accent:C.border}`,borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer'}}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── DASHBOARD TAB ── */}
      {tab==='dashboard'&&(
        <div>
          {/* KPI Stats */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:20}}>
            <StatCard label="Total Leads" value={stats.total_leads||0} icon="🎯" color={C.blue} />
            <StatCard label="This Month" value={stats.this_month_leads||0} icon="📅" color={C.accent} />
            <StatCard label="Converted" value={stats.converted||0} sub={`${convRate}% conversion`} icon="✅" color="#1B7A3E" />
            <StatCard label="Pipeline Value" value={inr(stats.pipeline_value||0)} icon="💰" color={C.amber} />
            <StatCard label="Revenue Closed" value={inr(stats.converted_revenue||0)} icon="🏆" color={C.primary} />
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
            {/* Leads by Stage */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
              <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:14}}>Lead Pipeline by Stage</div>
              {STAGES.map(stage=>{
                const stageData = (dashboard?.leads_by_stage||[]).find(s=>s.stage===stage);
                const count = parseInt(stageData?.count||0);
                const value = parseFloat(stageData?.value||0);
                const maxCount = Math.max(...(dashboard?.leads_by_stage||[]).map(s=>parseInt(s.count||0)),1);
                const s = STAGE_COLORS[stage]||{bg:C.bg,color:C.text3};
                return (
                  <div key={stage} style={{marginBottom:10}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                      <span style={{fontSize:12,fontWeight:600,color:C.text}}>{stage}</span>
                      <div style={{display:'flex',gap:10,alignItems:'center'}}>
                        <span style={{fontSize:12,color:C.text3}}>{inr(value)}</span>
                        <span style={{fontSize:13,fontWeight:800,color:s.color}}>{count}</span>
                      </div>
                    </div>
                    <div style={{height:6,background:C.bg,borderRadius:99,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${count/maxCount*100}%`,background:s.color,borderRadius:99,transition:'width .3s'}} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Social Media Leads */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
              <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:14}}>Social Media Leads</div>
              {(dashboard?.social_leads||[]).length===0?(
                <div style={{textAlign:'center',padding:20,color:C.text3}}>No social leads yet</div>
              ):(
                (dashboard?.social_leads||[]).map((sl,i)=>(
                  <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 12px',background:C.bg,borderRadius:8,marginBottom:6}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontSize:16}}>
                        {sl.platform==='facebook'?'👥':sl.platform==='instagram'?'📸':sl.platform==='whatsapp'?'💬':'🌐'}
                      </span>
                      <div>
                        <div style={{fontSize:12,fontWeight:700,color:C.text,textTransform:'capitalize'}}>{sl.platform}</div>
                        <div style={{fontSize:10,color:C.text3,textTransform:'capitalize'}}>{sl.status}</div>
                      </div>
                    </div>
                    <div style={{fontSize:18,fontWeight:900,color:C.accent}}>{sl.count}</div>
                  </div>
                ))
              )}
              <button onClick={()=>setTab('social')} style={{width:'100%',marginTop:10,padding:'8px',background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,fontSize:12,color:C.text2,cursor:'pointer',fontWeight:600}}>
                View All Social Leads →
              </button>
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
            {/* Bookings by type */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
              <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:14}}>Bookings This Month (by Type)</div>
              {(dashboard?.bookings_by_type||[]).length===0?(
                <div style={{textAlign:'center',padding:20,color:C.text3}}>No bookings this month</div>
              ):(
                (dashboard?.bookings_by_type||[]).map((b,i)=>(
                  <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'8px 12px',background:C.bg,borderRadius:8,marginBottom:6}}>
                    <span style={{fontSize:12,fontWeight:600,color:C.text}}>{b.booking_type}</span>
                    <div style={{display:'flex',gap:12}}>
                      <span style={{fontSize:12,color:C.text3}}>{b.count} bookings</span>
                      <span style={{fontSize:12,fontWeight:700,color:C.accent}}>{inr(b.revenue)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Recent Campaigns */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20}}>
              <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:14}}>Active Campaigns</div>
              {campaigns.filter(c=>c.status==='Active').length===0?(
                <div style={{textAlign:'center',padding:20,color:C.text3,fontSize:13}}>No active campaigns. <button onClick={()=>setTab('campaigns')} style={{background:'none',border:'none',color:C.accent,cursor:'pointer',fontSize:13}}>Create one →</button></div>
              ):(
                campaigns.filter(c=>c.status==='Active').slice(0,4).map(camp=>(
                  <div key={camp.id} style={{padding:'8px 12px',background:C.bg,borderRadius:8,marginBottom:6}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <div style={{fontSize:12,fontWeight:700,color:C.text}}>{camp.name}</div>
                      <div style={{fontSize:12,fontWeight:700,color:C.accent}}>{inr(camp.budget)}</div>
                    </div>
                    <div style={{fontSize:10,color:C.text3,marginTop:2}}>{camp.channel} · {camp.leads_generated} leads · {camp.conversions} converted</div>
                    <div style={{height:4,background:C.border,borderRadius:99,marginTop:6,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${Math.min(parseFloat(camp.spent)/parseFloat(camp.budget||1)*100,100)}%`,background:C.amber,borderRadius:99}} />
                    </div>
                    <div style={{fontSize:10,color:C.text3,marginTop:2}}>Spent: {inr(camp.spent)} of {inr(camp.budget)}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── LEADS TAB ── */}
      {tab==='leads'&&(
        <div>
          {/* Pipeline summary */}
          <div style={{display:'flex',gap:8,marginBottom:16,overflowX:'auto',paddingBottom:4}}>
            {STAGES.map(stage=>{
              const count = leads.filter(l=>l.stage===stage).length;
              const value = leads.filter(l=>l.stage===stage).reduce((s,l)=>s+parseFloat(l.value||0),0);
              const s = STAGE_COLORS[stage]||{bg:C.bg,color:C.text3};
              return (
                <div key={stage} onClick={()=>setFilterStage(filterStage===stage?'':stage)}
                  style={{flexShrink:0,padding:'10px 16px',background:filterStage===stage?s.color:C.surface,border:`2px solid ${filterStage===stage?s.color:C.border}`,borderRadius:10,cursor:'pointer',textAlign:'center',minWidth:100}}>
                  <div style={{fontSize:20,fontWeight:900,color:filterStage===stage?'#fff':s.color}}>{count}</div>
                  <div style={{fontSize:11,fontWeight:700,color:filterStage===stage?'rgba(255,255,255,.8)':C.text3}}>{stage}</div>
                  {value>0&&<div style={{fontSize:10,color:filterStage===stage?'rgba(255,255,255,.7)':C.text3,marginTop:2}}>{inr(value)}</div>}
                </div>
              );
            })}
          </div>

          {/* Filters */}
          <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search leads..."
              style={{padding:'8px 14px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:8,color:C.text,fontSize:13,width:220}} />
            <select value={filterSource} onChange={e=>setFilterSource(e.target.value)}
              style={{padding:'8px 12px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:8,color:C.text,fontSize:13}}>
              <option value="">All Sources</option>
              {SOURCES.map(s=><option key={s}>{s}</option>)}
            </select>
            {(filterStage||filterSource||search)&&(
              <button onClick={()=>{setFilterStage('');setFilterSource('');setSearch('');}}
                style={{padding:'8px 12px',background:C.redBg,color:C.red,border:'none',borderRadius:8,fontSize:12,cursor:'pointer'}}>Clear Filters</button>
            )}
          </div>

          {/* Leads table */}
          {filteredLeads.length===0?(
            <div style={{textAlign:'center',padding:50,background:C.surface,borderRadius:12,color:C.text3,border:`1px solid ${C.border}`}}>
              No leads found. Click "+ Add Lead" to add one.
            </div>
          ):(
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden'}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:C.bg}}>
                    {['Name','Phone','Source','Event Type','Value','Stage','Follow-up','Assigned','Actions'].map(h=>(
                      <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:C.text3,textTransform:'uppercase'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead,i)=>(
                    <tr key={lead.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.surface:'#F8FAF8'}}>
                      <td style={{padding:'10px 14px'}}>
                        <div style={{fontWeight:700,color:C.text,fontSize:13}}>{lead.name}</div>
                        {lead.email&&<div style={{fontSize:11,color:C.text3}}>{lead.email}</div>}
                      </td>
                      <td style={{padding:'10px 14px',color:C.text2,fontSize:12}}>{lead.phone||'—'}</td>
                      <td style={{padding:'10px 14px',color:C.text3,fontSize:12}}>{lead.source}</td>
                      <td style={{padding:'10px 14px',color:C.text2,fontSize:12}}>{lead.event_type||'—'}</td>
                      <td style={{padding:'10px 14px',fontWeight:700,color:C.accent}}>{lead.value?inr(lead.value):'—'}</td>
                      <td style={{padding:'10px 14px'}}>
                        <select value={lead.stage} onChange={e=>updateLeadStage(lead.id,e.target.value)}
                          style={{padding:'4px 8px',background:STAGE_COLORS[lead.stage]?.bg||C.bg,color:STAGE_COLORS[lead.stage]?.color||C.text,border:'none',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer'}}>
                          {STAGES.map(s=><option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={{padding:'10px 14px',color:lead.follow_up_date&&new Date(lead.follow_up_date)<new Date()?C.red:C.text3,fontSize:12}}>
                        {lead.follow_up_date?new Date(lead.follow_up_date).toLocaleDateString('en-IN'):'—'}
                      </td>
                      <td style={{padding:'10px 14px',color:C.text3,fontSize:12}}>{lead.assigned_to||'—'}</td>
                      <td style={{padding:'10px 14px'}}>
                        <button onClick={()=>{setEditItem(lead);setLeadForm({...initLead,...lead,follow_up_date:lead.follow_up_date?lead.follow_up_date.slice(0,10):''});setShowLeadForm(true);}}
                          style={{padding:'4px 10px',background:C.accentLight,color:C.accent,border:'none',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── CAMPAIGNS TAB ── */}
      {tab==='campaigns'&&(
        <div>
          {campaigns.length===0?(
            <div style={{textAlign:'center',padding:50,background:C.surface,borderRadius:12,color:C.text3,border:`1px solid ${C.border}`}}>
              No campaigns yet. Click "+ New Campaign" to create one.
            </div>
          ):(
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:14}}>
              {campaigns.map(camp=>{
                const roi = parseFloat(camp.spent||0)>0 ? Math.round(parseFloat(camp.revenue_generated||0)/parseFloat(camp.spent||1)*100) : 0;
                const budgetUsed = parseFloat(camp.budget||0)>0 ? Math.round(parseFloat(camp.spent||0)/parseFloat(camp.budget||1)*100) : 0;
                return (
                  <div key={camp.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20,borderLeft:`4px solid ${camp.status==='Active'?C.accent:camp.status==='Completed'?C.blue:C.amber}`}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
                      <div>
                        <div style={{fontSize:14,fontWeight:800,color:C.text}}>{camp.name}</div>
                        <div style={{fontSize:11,color:C.text3}}>{camp.channel} · {camp.type}</div>
                      </div>
                      <span style={{padding:'3px 10px',borderRadius:99,fontSize:11,fontWeight:700,
                        background:camp.status==='Active'?C.accentLight:camp.status==='Completed'?C.blueBg:C.amberBg,
                        color:camp.status==='Active'?C.accent:camp.status==='Completed'?C.blue:C.amber}}>
                        {camp.status}
                      </span>
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12,fontSize:12}}>
                      <div style={{padding:'6px 10px',background:C.bg,borderRadius:6}}>
                        <div style={{color:C.text3}}>Budget</div>
                        <div style={{fontWeight:700,color:C.text}}>{inr(camp.budget)}</div>
                      </div>
                      <div style={{padding:'6px 10px',background:C.bg,borderRadius:6}}>
                        <div style={{color:C.text3}}>Spent</div>
                        <div style={{fontWeight:700,color:C.amber}}>{inr(camp.spent)}</div>
                      </div>
                      <div style={{padding:'6px 10px',background:C.bg,borderRadius:6}}>
                        <div style={{color:C.text3}}>Leads</div>
                        <div style={{fontWeight:700,color:C.blue}}>{camp.leads_generated}</div>
                      </div>
                      <div style={{padding:'6px 10px',background:C.bg,borderRadius:6}}>
                        <div style={{color:C.text3}}>ROI</div>
                        <div style={{fontWeight:700,color:roi>100?C.accent:C.red}}>{roi}%</div>
                      </div>
                    </div>
                    {/* Budget progress */}
                    <div style={{marginBottom:10}}>
                      <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:C.text3,marginBottom:3}}>
                        <span>Budget Used</span><span>{budgetUsed}%</span>
                      </div>
                      <div style={{height:5,background:C.border,borderRadius:99,overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${Math.min(budgetUsed,100)}%`,background:budgetUsed>90?C.red:budgetUsed>70?C.amber:C.accent,borderRadius:99}} />
                      </div>
                    </div>
                    <button onClick={()=>{setEditItem(camp);setCampaignForm({...initCampaign,...camp,start_date:camp.start_date?camp.start_date.slice(0,10):'',end_date:camp.end_date?camp.end_date.slice(0,10):''});setShowCampaignForm(true);}}
                      style={{width:'100%',padding:'7px',background:C.accentLight,color:C.accent,border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>
                      Edit Campaign
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── SOCIAL MEDIA TAB ── */}
      {tab==='social'&&(
        <div>
          {/* Social platform stats */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
            {['facebook','instagram','whatsapp','google'].map(platform=>{
              const platformLeads = socialLeads.filter(l=>l.platform===platform);
              const newLeads = platformLeads.filter(l=>l.status==='new').length;
              const converted = platformLeads.filter(l=>l.status==='converted').length;
              const icons = {facebook:'👥',instagram:'📸',whatsapp:'💬',google:'🔍'};
              return (
                <div key={platform} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                    <span style={{fontSize:24}}>{icons[platform]||'🌐'}</span>
                    <div style={{fontSize:14,fontWeight:800,color:C.text,textTransform:'capitalize'}}>{platform}</div>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                    <div style={{textAlign:'center',padding:'6px',background:C.bg,borderRadius:6}}>
                      <div style={{fontSize:18,fontWeight:800,color:C.blue}}>{newLeads}</div>
                      <div style={{fontSize:10,color:C.text3}}>New</div>
                    </div>
                    <div style={{textAlign:'center',padding:'6px',background:C.accentLight,borderRadius:6}}>
                      <div style={{fontSize:18,fontWeight:800,color:C.accent}}>{converted}</div>
                      <div style={{fontSize:10,color:C.text3}}>Converted</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Social leads table */}
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden'}}>
            <div style={{padding:'14px 18px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontSize:14,fontWeight:800,color:C.text}}>Social Media Leads ({socialLeads.length})</div>
            </div>
            {socialLeads.length===0?(
              <div style={{textAlign:'center',padding:40,color:C.text3}}>No social leads yet. Connect Facebook/Instagram to auto-capture leads.</div>
            ):(
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:C.bg}}>
                    {['Platform','Name','Phone','Email','Campaign','Ad','Status','Date'].map(h=>(
                      <th key={h} style={{padding:'9px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:C.text3,textTransform:'uppercase'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {socialLeads.map((lead,i)=>(
                    <tr key={lead.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.surface:'#F8FAF8'}}>
                      <td style={{padding:'9px 14px'}}>
                        <span style={{padding:'3px 10px',borderRadius:99,fontSize:11,fontWeight:700,
                          background:lead.platform==='facebook'?'#E7F0FD':lead.platform==='instagram'?'#FDE7F0':'#E7FDE7',
                          color:lead.platform==='facebook'?'#1877F2':lead.platform==='instagram'?'#E1306C':'#25D366',
                          textTransform:'capitalize'}}>
                          {lead.platform}
                        </span>
                      </td>
                      <td style={{padding:'9px 14px',fontWeight:700,color:C.text,fontSize:13}}>{lead.name||'—'}</td>
                      <td style={{padding:'9px 14px',color:C.text2,fontSize:12}}>{lead.phone||'—'}</td>
                      <td style={{padding:'9px 14px',color:C.text3,fontSize:11}}>{lead.email||'—'}</td>
                      <td style={{padding:'9px 14px',color:C.text3,fontSize:11}}>{lead.campaign_name||'—'}</td>
                      <td style={{padding:'9px 14px',color:C.text3,fontSize:11}}>{lead.ad_name||'—'}</td>
                      <td style={{padding:'9px 14px'}}>
                        <span style={{padding:'3px 8px',borderRadius:99,fontSize:10,fontWeight:700,
                          background:lead.status==='converted'?C.accentLight:lead.status==='new'?C.blueBg:C.amberBg,
                          color:lead.status==='converted'?C.accent:lead.status==='new'?C.blue:C.amber,
                          textTransform:'capitalize'}}>
                          {lead.status}
                        </span>
                      </td>
                      <td style={{padding:'9px 14px',color:C.text3,fontSize:11}}>{new Date(lead.created_at).toLocaleDateString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── CONTENT CALENDAR TAB ── */}
      {tab==='content'&&(
        <div>
          {showContentForm&&(
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20,marginBottom:16}}>
              <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:14}}>Schedule Content</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:10}}>
                <div style={{gridColumn:'span 3'}}>{lbl('Title *')}<input style={inp} value={contentForm.title} onChange={e=>setContentForm(p=>({...p,title:e.target.value}))} placeholder="Post title or caption" /></div>
                <div>{lbl('Platform')}<select style={inp} value={contentForm.platform} onChange={e=>setContentForm(p=>({...p,platform:e.target.value}))}>{PLATFORMS.map(s=><option key={s}>{s}</option>)}</select></div>
                <div>{lbl('Content Type')}<select style={inp} value={contentForm.content_type} onChange={e=>setContentForm(p=>({...p,content_type:e.target.value}))}>{CONTENT_TYPES.map(s=><option key={s}>{s}</option>)}</select></div>
                <div>{lbl('Assigned To')}<input style={inp} value={contentForm.assigned_to} onChange={e=>setContentForm(p=>({...p,assigned_to:e.target.value}))} /></div>
                <div>{lbl('Date')}<input style={inp} type="date" value={contentForm.scheduled_date} onChange={e=>setContentForm(p=>({...p,scheduled_date:e.target.value}))} /></div>
                <div>{lbl('Time')}<input style={inp} type="time" value={contentForm.scheduled_time} onChange={e=>setContentForm(p=>({...p,scheduled_time:e.target.value}))} /></div>
                <div>{lbl('Tags')}<input style={inp} value={contentForm.tags} onChange={e=>setContentForm(p=>({...p,tags:e.target.value}))} placeholder="#botanix #wedding" /></div>
                <div style={{gridColumn:'span 3'}}>{lbl('Content / Caption')}<textarea style={{...inp,height:70,resize:'vertical'}} value={contentForm.content} onChange={e=>setContentForm(p=>({...p,content:e.target.value}))} /></div>
              </div>
              <div style={{display:'flex',gap:10}}>
                <button onClick={saveContent} disabled={saving} style={{flex:1,padding:'10px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>Save</button>
                <button onClick={()=>setShowContentForm(false)} style={{flex:1,padding:'10px',background:'transparent',color:C.text2,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,cursor:'pointer'}}>Cancel</button>
              </div>
            </div>
          )}

          {content.length===0?(
            <div style={{textAlign:'center',padding:50,background:C.surface,borderRadius:12,color:C.text3,border:`1px solid ${C.border}`}}>
              No content scheduled yet. Click "+ Schedule Content" to add.
            </div>
          ):(
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,overflow:'hidden'}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{background:C.bg}}>
                    {['Date','Platform','Type','Title','Assigned','Status'].map(h=>(
                      <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:C.text3,textTransform:'uppercase'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {content.map((item,i)=>(
                    <tr key={item.id} style={{borderBottom:`1px solid ${C.border}`,background:i%2===0?C.surface:'#F8FAF8'}}>
                      <td style={{padding:'10px 14px',color:C.text2,fontSize:12}}>{item.scheduled_date?new Date(item.scheduled_date).toLocaleDateString('en-IN'):'—'}</td>
                      <td style={{padding:'10px 14px'}}>
                        <span style={{padding:'2px 8px',borderRadius:99,fontSize:11,fontWeight:700,background:C.blueBg,color:C.blue}}>{item.platform}</span>
                      </td>
                      <td style={{padding:'10px 14px',color:C.text3,fontSize:12}}>{item.content_type}</td>
                      <td style={{padding:'10px 14px',fontWeight:600,color:C.text,fontSize:13}}>{item.title}</td>
                      <td style={{padding:'10px 14px',color:C.text3,fontSize:12}}>{item.assigned_to||'—'}</td>
                      <td style={{padding:'10px 14px'}}>
                        <span style={{padding:'2px 8px',borderRadius:99,fontSize:11,fontWeight:700,background:item.status==='Published'?C.accentLight:C.amberBg,color:item.status==='Published'?C.accent:C.amber}}>{item.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── BUDGET TAB ── */}
      {tab==='budget'&&(
        <div>
          {showBudgetForm&&(
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:20,marginBottom:16,maxWidth:500}}>
              <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:14}}>Add / Update Budget</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                <div>{lbl('Month')}<select style={inp} value={budgetForm.month} onChange={e=>setBudgetForm(p=>({...p,month:parseInt(e.target.value)}))}>{MONTH_NAMES.map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}</select></div>
                <div>{lbl('Year')}<input style={inp} type="number" value={budgetForm.year} onChange={e=>setBudgetForm(p=>({...p,year:parseInt(e.target.value)}))} /></div>
                <div>{lbl('Channel')}<select style={inp} value={budgetForm.channel} onChange={e=>setBudgetForm(p=>({...p,channel:e.target.value}))}>{CHANNELS.map(s=><option key={s}>{s}</option>)}</select></div>
                <div>{lbl('Allocated (Rs)')}<input style={inp} type="number" value={budgetForm.allocated} onChange={e=>setBudgetForm(p=>({...p,allocated:e.target.value}))} /></div>
                <div>{lbl('Spent (Rs)')}<input style={inp} type="number" value={budgetForm.spent} onChange={e=>setBudgetForm(p=>({...p,spent:e.target.value}))} /></div>
                <div>{lbl('Notes')}<input style={inp} value={budgetForm.notes} onChange={e=>setBudgetForm(p=>({...p,notes:e.target.value}))} /></div>
              </div>
              <div style={{display:'flex',gap:10}}>
                <button onClick={saveBudget} disabled={saving} style={{flex:1,padding:'10px',background:C.accent,color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>Save</button>
                <button onClick={()=>setShowBudgetForm(false)} style={{flex:1,padding:'10px',background:'transparent',color:C.text2,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,cursor:'pointer'}}>Cancel</button>
              </div>
            </div>
          )}

          {/* Budget summary */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:12}}>
            {budgets.length===0?(
              <div style={{gridColumn:'span 3',textAlign:'center',padding:50,background:C.surface,borderRadius:12,color:C.text3,border:`1px solid ${C.border}`}}>
                No budgets set. Click "+ Add Budget" to allocate marketing budget by channel.
              </div>
            ):budgets.map(b=>{
              const pct = parseFloat(b.allocated||0)>0?Math.round(parseFloat(b.spent||0)/parseFloat(b.allocated||1)*100):0;
              return (
                <div key={b.id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:18}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
                    <div style={{fontSize:14,fontWeight:800,color:C.text}}>{b.channel}</div>
                    <div style={{fontSize:12,color:C.text3}}>{MONTH_NAMES[b.month-1]} {b.year}</div>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:10}}>
                    <div><div style={{fontSize:10,color:C.text3}}>Allocated</div><div style={{fontSize:16,fontWeight:800,color:C.text}}>{inr(b.allocated)}</div></div>
                    <div><div style={{fontSize:10,color:C.text3}}>Spent</div><div style={{fontSize:16,fontWeight:800,color:pct>90?C.red:C.amber}}>{inr(b.spent)}</div></div>
                  </div>
                  <div style={{height:6,background:C.border,borderRadius:99,overflow:'hidden',marginBottom:4}}>
                    <div style={{height:'100%',width:`${Math.min(pct,100)}%`,background:pct>90?C.red:pct>70?C.amber:C.accent,borderRadius:99}} />
                  </div>
                  <div style={{fontSize:11,color:C.text3,display:'flex',justifyContent:'space-between'}}>
                    <span>{pct}% used</span>
                    <span>Remaining: {inr(parseFloat(b.allocated||0)-parseFloat(b.spent||0))}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showLeadForm&&<LeadModal />}
      {showCampaignForm&&<CampaignModal />}
    </div>
  );
}
